import { useCachedFetch } from '../../shared/hooks';
import apiRoutes from '../../../config/api/routes';
import { getPagesMapper, Page } from '../../../config/api/models/Page';
import {
  TrajectorySectionOrWorksheetType,
  TrajectorySectionType,
} from '../../../config/api/models/Trajectory';
import { useEffect, useMemo, useState } from 'react';
import { RefetchOptions } from 'react-query/types/core/query';
import { useAppDispatch, useAppSelector } from '../../shared/hooks/useAppDispatch';
import { setCurrentPageId } from 'src/modules/shared/slices/trajectories/entity/trajectorySlice';
import { useParams } from 'react-router';
import { UrlParams } from 'src/modules/shared/types';

export type PagesByTypeResponse = {
  initial: boolean;
  pending: boolean;
  success: boolean;
  error: boolean;
  data?: Page[];
  refetch: (options?: RefetchOptions) => Promise<Page[] | undefined>;
};

export const enabledPages: string[] = [
  'opbouw',
  'theory',
  'study_guide',
  'worksheet_exercises',
  'study_trainer',
];

export const usePagesByType = (
  pageType: TrajectorySectionOrWorksheetType | null
): PagesByTypeResponse => {
  const { trajectoryId } = useParams<UrlParams>();
  const { viewMode, currentTrajectoryId } = useAppSelector((state) => state.trajectories);
  const [currentViewMode, setCurrentViewMode] = useState(viewMode);
  const dispatch = useAppDispatch();

  const pagesFetch = useCachedFetch(
    ['getPages', trajectoryId ?? currentTrajectoryId, pageType],
    apiRoutes.pages(
      (trajectoryId ?? currentTrajectoryId) as string,
      pageType as TrajectorySectionType
    ),
    {
      mapper: getPagesMapper,
      options: {
        enabled: enabledPages.includes(pageType ?? ''),
        refetchOnWindowFocus: false,
      },
    }
  );

  useEffect(() => {
    if (viewMode !== currentViewMode) {
      pagesFetch.refetch();
      setCurrentViewMode(viewMode);
    }
  }, [viewMode, currentViewMode, pagesFetch]);

  useEffect(() => {
    if (pagesFetch.data && pagesFetch.isSuccess) {
      dispatch(setCurrentPageId(pagesFetch.data?.[0]?.id));
    }
  }, [pagesFetch.data, pagesFetch.isSuccess, dispatch]);

  return useMemo(
    () => ({
      initial: !pagesFetch.isLoading && !pagesFetch.isError && !pagesFetch.isSuccess,
      pending: pagesFetch.isLoading || pagesFetch.isFetching,
      error: pagesFetch.isError,
      success: pagesFetch.isSuccess,
      data: pagesFetch.data,
      refetch: pagesFetch.refetch,
    }),
    [pagesFetch]
  );
};
